import { useEffect, useRef, useState } from "react";
import { endpoints } from "../api.js";
import { t } from "../i18n.js";
import "../propertyAi.css";

const factKeys = new Set(["propertyType", "purpose", "priceOMR", "areaSqm", "bedrooms", "bathrooms", "parkingSpaces", "governorate", "wilayat", "neighborhood"]);
const invalid = () => new Error("AI is temporarily unavailable. The property information is still available.");
export default function PropertyAiSummary({ listingId, language = "en", services = endpoints, onRefreshDetails }) {
  const [result, setResult] = useState(null), [error, setError] = useState(""), [pending, setPending] = useState(false);
  const busy = useRef(false), generation = useRef(0), controller = useRef(null), feedback = useRef(null);
  useEffect(() => {
    generation.current += 1; controller.current?.abort(); busy.current = false;
    setResult(null); setError(""); setPending(false);
    return () => { generation.current += 1; controller.current?.abort(); };
  }, [listingId, language, services]);
  async function generate() {
    if (busy.current) return;
    busy.current = true; const version = ++generation.current;
    controller.current = new AbortController(); setPending(true); setError(""); setResult(null);
    try {
      const response = await services.aiPropertySummary({ listingId, language }, { signal: controller.current.signal });
      if (version !== generation.current) return;
      if (response?.available !== true || !["live", "demo"].includes(response.status) || response.source?.listingId !== listingId || response.source?.projectionVersion !== 2
          || !Array.isArray(response.facts) || !response.facts.length || response.facts.some(fact => !factKeys.has(fact.key) || typeof fact.label !== "string" || typeof fact.value !== "string")) throw invalid();
      setResult(response);
    } catch (failure) {
      if (version !== generation.current) return;
      setError(failure.status === 404 ? "This property is unavailable for AI summary." : [401, 403].includes(failure.status) ? "You cannot run this private AI analysis." : failure.status === 409 ? "The property changed. Refresh its details before trying again." : "AI is temporarily unavailable. The property information is still available.");
    } finally {
      if (version === generation.current) {
        busy.current = false; setPending(false);
        requestAnimationFrame(() => feedback.current?.focus());
      }
    }
  }
  return <section className="property-ai" dir={language === "ar" ? "rtl" : "ltr"} aria-label={t(language, "aiSummary")} aria-busy={pending}>
    <h2>{t(language, "aiSummary")}</h2>
    <p>{t(language, "Generated summaries use current public property facts. They do not verify the property, ownership or legal eligibility.")}</p>
    <div className="property-ai-feedback" ref={feedback} tabIndex={-1} role="status" aria-live="polite">
      {pending && <p>{t(language, "Generating summary…")}</p>}
      {error && <p>{t(language, error)}</p>}
      {result && <><strong className="property-ai-mode">{t(language, result.status === "demo" ? "Demo summary — no live AI generation" : "AI-generated summary — unverified")}</strong>
        <dl>{result.facts.map(fact => <div key={fact.key}><dt>{fact.label}</dt><dd><bdi>{fact.value}</bdi></dd></div>)}</dl></>}
      {!pending && !error && !result && <p>{t(language, "A current AI summary has not been generated. Use the property details above.")}</p>}
    </div>
    <button className="primary" type="button" disabled={pending} onClick={generate}>{t(language, pending ? "Generating summary…" : error ? "Try summary again" : result ? "Generate a fresh summary" : "Generate AI summary")}</button>
    {error === "The property changed. Refresh its details before trying again." && onRefreshDetails && <button type="button" onClick={onRefreshDetails}>{t(language, "Refresh property details")}</button>}
  </section>;
}
